interface LoginHintParams {
  /**
   * Flag to indicate that the user should be redirected to IAM for authentication (if needed). Value doesn't matter, just used as a flag.
   */
  iamRedirect?: string,
  /**
   * The username login hint to use for IAM authentication.
   */
  login_hint?: string,
  /**
   * The Verint VEID hint to use for IAM authentication.
   */
  v_veid_hint?: string,
  /**
   * Flag to indicate that the user should be redirected to VCP Identity for authentication (if needed). Value doesn't matter, just used as a flag.
   */
  vcp_auth?: string,
}

interface LoginHintUrlParamResult {
  loginHintParams: LoginHintParams,
  rebuiltUrl: string,
}

export const IAM_REDIRECT_SESSION_STORAGE_KEY = 'iamRedirect'
export const LOGIN_HINT_SESSION_STORAGE_KEY = 'loginHint'
export const VEID_HINT_SESSION_STORAGE_KEY = 'veidHint'
export const VCP_AUTH_SESSION_STORAGE_KEY = 'vcp_auth'

/**
 * Handles IAM redirect by extracting login hint parameters from the URL and storing them in sessionStorage.
 * Also rebuilds the URL history without the login hint parameters.
 */
export const handleIamRedirect = (): void => {
  const loginHintResult = extractLoginHintParamsFromUrl()
  if (loginHintResult) {
    // Add a flag to sessionStorage to indicate that the user should be redirected to IAM for authentication (if needed).
    sessionStorage.setItem(IAM_REDIRECT_SESSION_STORAGE_KEY, 'true')

    // Add the login hints to sessionStorage if they exist
    if (loginHintResult.loginHintParams.login_hint) {
      sessionStorage.setItem(LOGIN_HINT_SESSION_STORAGE_KEY, loginHintResult.loginHintParams.login_hint)
    }
    if (loginHintResult.loginHintParams.v_veid_hint) {
      sessionStorage.setItem(VEID_HINT_SESSION_STORAGE_KEY, loginHintResult.loginHintParams.v_veid_hint)
    }
    if (loginHintResult.loginHintParams.vcp_auth) {
      sessionStorage.setItem(VCP_AUTH_SESSION_STORAGE_KEY, 'true')
    }

    // Rebuild the URL without the login hint parameters as they are no longer needed and can cause issues depending on how location and
    // history are handled in the application.
    window.history.replaceState({}, '', loginHintResult.rebuiltUrl)
  }
}

/**
 * Extracts login hint parameters from the current URL, including those in the hash fragment.
 * Parameters in the main URL take precedence over those in the hash fragment. Also generates a
 * rebuilt URL without the login hint parameters.
 *
 * @returns An object containing all parameters from the URL and hash fragment and a rebuilt URL,
 *          or undefined if no login hint parameters are found.
 */
const extractLoginHintParamsFromUrl = (): LoginHintUrlParamResult|undefined => {
  const url = new URL(window.location.href)
  const mainParamsObj =  Object.fromEntries(url.searchParams)

  const hash = window.location.hash
  const hashParams = hash ? new URLSearchParams(hash.split('?')[1] || '') : new URLSearchParams()

  const { iamRedirect = undefined, login_hint = undefined, v_veid_hint = undefined, vcp_auth = undefined } = {
    ...Object.fromEntries(hashParams),
    ...mainParamsObj,
  }
  const loginHintParams: LoginHintParams = {
    iamRedirect,
    login_hint,
    v_veid_hint,
    vcp_auth,
  }

  if (Object.values(loginHintParams).some(value => value !== undefined)) {
    // Remove LoginHintParams from main URL params
    const paramsToRemove = Object.keys(loginHintParams)
    paramsToRemove.forEach(param => url.searchParams.delete(param))

    // Remove LoginHintParams from hash params
    paramsToRemove.forEach(param => hashParams.delete(param))

    // Rebuild the URL with cleaned params
    const baseUrl = url.toString()
    const newHash = hash.split('?')[0] + (hashParams.size > 0 ? '?' + hashParams.toString() : '')
    const rebuiltUrl = baseUrl.split('#')[0] + newHash

    return {
      loginHintParams: {
        iamRedirect,
        login_hint,
        v_veid_hint,
        vcp_auth,
      },
      rebuiltUrl,
    }
  }

  return undefined
}
